import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Outlet, useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";

import { fetchCarts } from "../api/carts";
import { listImportJobs } from "../api/imports";
import { CartTable } from "./CartPage";
import { useSession } from "../auth/useSession";
import { HubTabs } from "../components/AppNavigation";
import PurchaseOrdersTab from "../components/PurchaseOrdersTab";
import { isDemoMode } from "../lib/env";
import { HubContext } from "../lib/hubContext";

// Zusammengelegte Seiten (#630): eine Kopfzeilen-Schaltfläche, darunter Reiter
// für die früher einzelnen Seiten. Die Seiten selbst bleiben vorerst wie sie
// sind und werden nach und nach vereinfacht.

/** Bestellungen · Preisanfragen · Angebote · Auftragsbestätigungen. */
export function OrdersHubLayout() {
  const { pathname } = useLocation();
  const [params, setParams] = useSearchParams();
  // Einkauf sieht seine Bestellungen; Controlling und Administration auch alle.
  const canSeeAll = useSession().user?.active_membership.can_review_prices === true;
  const at = (path: string) => pathname === path || pathname.startsWith(`${path}/`);
  const onOrders = at("/bestellungen");
  const scoped = onOrders || at("/preisanfrage");
  const scope = canSeeAll && params.get("alle") === "1" ? "company" : "mine";
  return (
    <HubContext.Provider value={true}>
      <HubHeader
        title="Bestellungen"
        aside={scoped && canSeeAll ? (
          <div role="group" aria-label="Bestellungen anzeigen" className="flex rounded-md border border-slate-300 bg-white p-0.5 text-sm shadow-sm">
            {(["mine", "company"] as const).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={scope === value}
                onClick={() => setParams(value === "company" ? { alle: "1" } : {}, { replace: true })}
                className={`rounded px-3 py-1 font-medium ${scope === value ? "bg-emerald-600 text-white" : "text-slate-600 hover:text-slate-900"}`}
              >
                {value === "mine" ? "Meine" : "Alle"}
              </button>
            ))}
          </div>
        ) : null}
      />
      <HubTabs
        label="Bestellungen"
        tabs={[
          { label: "Bestellungen", to: "/bestellungen", active: onOrders || at("/orders") },
          { label: "Preisanfragen", to: "/preisanfrage", active: at("/preisanfrage") },
          { label: "Angebote", to: "/angebote", active: pathname === "/angebote" },
          // Ohne Anzahl: der offene Bestand ist Altbestand, kein Arbeitsvorrat.
          { label: "Auftragsbestätigungen", to: "/auftragsbestaetigungen", active: at("/auftragsbestaetigungen") },
        ]}
      />
      <Outlet context={scope} />
    </HubContext.Provider>
  );
}

/**
 * Preisanfragen sind Warenkörbe mit Status „angefragt“ (#547): Export oder
 * E-Mail-Entwurf aus dem Warenkorb, kein eigener Zwischenspeicher mehr.
 */
export function PriceRequestsListPage() {
  const navigate = useNavigate();
  const scope = useOutletContext<"mine" | "company">();
  const query = useQuery({
    queryKey: ["carts", "list", scope, null, "requested"],
    queryFn: () => fetchCarts(scope, null, "requested"),
  });
  return (
    <main className="mx-auto max-w-7xl px-6 py-5">
      {query.isLoading ? <p className="text-sm text-slate-600">Lade Preisanfragen …</p> : null}
      {query.isError ? <p role="alert" className="text-sm text-rose-700">Die Preisanfragen konnten nicht geladen werden.</p> : null}
      {query.data ? (
        <CartTable
          carts={query.data.items}
          onOpen={(cartId) => navigate(`/warenkorb?cart=${encodeURIComponent(cartId)}`)}
          emptyText="Keine offene Preisanfrage. Im Warenkorb wird über „Als angefragt markieren“ eine daraus."
        />
      ) : null}
    </main>
  );
}

export function OrdersListPage() {
  const navigate = useNavigate();
  const scope = useOutletContext<"mine" | "company">();
  return (
    <main className="mx-auto max-w-7xl px-6 py-5">
      <PurchaseOrdersTab
        scope={scope}
        onCartCreated={(cartId) => navigate(`/warenkorb?cart=${encodeURIComponent(cartId)}`)}
      />
    </main>
  );
}

function HubHeader({ title, aside }: { title: string; aside?: ReactNode }) {
  return (
    <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-6 pt-6">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{title}</h1>
      {aside}
    </div>
  );
}

/** Hochladen · Importe · Datenqualität · Dateibestand. */
export function UploadsHubLayout() {
  const { pathname } = useLocation();
  const membership = useSession().user?.active_membership;
  const admin = membership?.can_manage_imports === true;
  const pending = useQuery({
    queryKey: ["imports", "jobs", "cockpit"],
    queryFn: () => listImportJobs(),
    enabled: membership?.can_review_prices === true && !isDemoMode(),
    retry: false,
    staleTime: 60_000,
  });
  const pendingCount = (pending.data ?? []).filter((job) => job.status === "review").length;
  return (
    <HubContext.Provider value={true}>
      <HubHeader title="Belege hochladen" />
      <HubTabs
        label="Belege hochladen"
        tabs={[
          { label: "Hochladen", to: "/imports", active: pathname === "/imports" },
          { label: "Importe", to: "/imports/verlauf", active: pathname === "/imports/verlauf", count: pendingCount },
          ...(admin
            ? [
                { label: "Datenqualität", to: "/imports/review", active: pathname === "/imports/review" },
                { label: "Dateibestand", to: "/coverage", active: pathname === "/coverage" },
              ]
            : []),
        ]}
      />
      <Outlet />
    </HubContext.Provider>
  );
}
